import { memo, useCallback, useEffect, useRef, useState } from 'react';

import dynamic from 'next/dynamic';
import { fetchPaginatedNoteListData } from 'components/landing/selected-note-list/data-fetcher';
import Note from 'components/landing/selected-note-list/note';
import PropTypes from 'prop-types';
import Spacer from 'core/spacer';
import styles from 'styles/SearchBox.module.scss';
import Title from 'core/title';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import { usePageParam } from 'hooks/useUrlParams';
import { useTranslations } from 'next-intl';

const Spinner = dynamic(() => import('core/buttons/spinner'));

const NoteListSection = ({ pageCode, activeSearch }) => {
    const [itemList, setItemList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const [requestAvailable, setRequestAvailable] = useState(true);
    const isRequestInProgress = useRef(false);
    const t = useTranslations('selected-note-list');

    const [currentPage, setCurrentPage] = usePageParam('page', 1);

    const handleRequest = useCallback(() => {
        if (!requestAvailable || loading || isRequestInProgress.current) {
            return;
        }

        isRequestInProgress.current = true;
        let nextPage = currentPage;
        if (!isFirstLoad) {
            nextPage = currentPage + 1;
        }
        setLoading(true);

        const params = {
            page: nextPage,
            page_code: pageCode,
            is_search: false
        };

        const handlePaginationSuccess = (result, nextPage) => {
            if (result.results.length > 0) {
                setItemList((prevItems) => [...prevItems, ...result.results]);
                setCurrentPage(nextPage);
            }

            if (result.next === null || result.results.length === 0) {
                setRequestAvailable(false);
            }
        };

        const handlePaginationError = () => {
            setRequestAvailable(false);
        };

        const handlePaginationLoading = (isLoading) => {
            // isLoading is a boolean
            setLoading(isLoading);
            if (!isLoading) {
                isRequestInProgress.current = false;
            }
        };

        fetchPaginatedNoteListData(
            params,
            nextPage,
            handlePaginationSuccess,
            handlePaginationError,
            handlePaginationLoading
        );
    }, [requestAvailable, loading, currentPage, pageCode, isFirstLoad]);

    const observerRef = useIntersectionObserver(() => {
        if (!loading && !isRequestInProgress.current) {
            handleRequest();
        }
    }, [loading, requestAvailable]);

    useEffect(() => {
        handleRequest();
        setLoading(true);
        setIsFirstLoad(false);
    }, [pageCode]);

    if (itemList === undefined || itemList === null || itemList.length === 0) {
        if (loading) {
            return (
                <div className={styles.observed_div}>
                    <Spinner sizeBig />
                </div>
            );
        }
        return null;
    }

    return (
        <>
            {/* Index notes */}
            {activeSearch && (
                // Load title only if there is an active search
                <>
                    <Spacer space={6} />
                    <Title content={t('other-notes-title')} />
                </>
            )}

            <div className={styles.note_container}>
                {itemList.map((note) => (
                    <Note key={note.url} item={note} />
                ))}
            </div>
            <div ref={observerRef} className={styles.observed_div}>
                {loading && <Spinner sizeBig />}
            </div>
        </>
    );
};

NoteListSection.propTypes = {
    pageCode: PropTypes.string.isRequired,
    activeSearch: PropTypes.bool.isRequired
};

export default memo(NoteListSection);
